import { useEffect, useState } from 'react';
import {
	Box,
	Flex,
	SimpleGrid,
	Text,
	useBreakpointValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';

import {
	ItemBidModel,
	ItemAskModel,
	UserModel,
} from 'models';
import EmptyState from '../EmptyState/EmptyState';
import ItemAskCard from '../Item/ItemAskCard/ItemAskCard';
import ItemBidCard from '../Item/ItemBidCard/ItemBidCard';
import TradingFloorFilters from './TradingFloorFilters';
import TradingFloorSkeleton from './TradingFloorSkeleton';
import { filterItemsByQuery } from 'utils/search';
import { sortByVerificationAndExpiration } from 'utils/activemarket';

export interface TradingFloorProps {
	bids: ItemBidModel[];
	asks: ItemAskModel[];
	user?: UserModel;
	showSearchFilter?: boolean;
	onItemNameClick?: (itemId: string, itemName: string) => void;
	onItemArtistNameClick?: (artistId: string, artistName: string) => void;
	onRemoveAskClick?: (ask: ItemAskModel) => void;
	onRemoveBidClick?: (bid: ItemBidModel) => void;
	onEditAskClick?: (ask: ItemAskModel) => void;
	onEditBidClick?: (bid: ItemBidModel) => void;
	onContactBidClick?: (bid: ItemBidModel) => void;
	onSellBidClick?: (bid: ItemBidModel) => void;
	onBuyAskClick?: (ask: ItemAskModel) => void;
	onContactAskClick?: (ask: ItemAskModel) => void;
	isLoading?: boolean;
	loadingRows?: number;
}

function ColumnContainer({
	title,
	children,
	color = 'gray',
}: {
	title: string;
	children: React.ReactNode;
	color?: string;
}) {
	return (
		<Flex
			direction="column"
			height="100%"
			borderRadius="md"
			overflow="hidden"
			bg="white"
		>
			<Box
				px={3}
				py={2}
				bg={`${color}.50`}
				border="1px solid"
				borderColor={`${color}.700`}
				borderRadius="md"
				textAlign={"center"}
			>
				<Text fontWeight="bold" fontSize="md" color={`${color}.700`}>
					{title}
				</Text>
			</Box>
			<Box flex="1" overflowY="auto" p={3}>
				<SimpleGrid columns={1} spacing={3}>
					{children}
				</SimpleGrid>
			</Box>
		</Flex>
	);
}

const MotionBox = motion(Box);

export default function TradingFloor({
	bids = [],
	asks = [],
	user,
	showSearchFilter,
	onItemNameClick,
	onItemArtistNameClick,
	onRemoveAskClick,
	onRemoveBidClick,
	onEditAskClick,
	onEditBidClick,
	onSellBidClick,
	onContactBidClick,
	onBuyAskClick,
	onContactAskClick,
	isLoading = false,
	loadingRows = 4,
}: TradingFloorProps) {
	const isMobile = useBreakpointValue({ base: true, md: false });
	const MOBILE_COLUMN_PX = 600;
	const [filteredBids, setFilteredBids] = useState(bids);
	const [filteredAsks, setFilteredAsks] = useState(asks);
	const [showBids, setShowBids] = useState(false);
	const [showAsks, setShowAsks] = useState(false);

	useEffect(() => {
		if (bids.length) setFilteredBids(sortByVerificationAndExpiration(bids));
		if (asks.length) setFilteredAsks(sortByVerificationAndExpiration(asks));
	}, [bids, asks]);

	const handleSearchChange = (q: string) => {
		setFilteredBids(
			bids.filter((b) => b.item && filterItemsByQuery([b.item], q).length)
		);
		setFilteredAsks(
			asks.filter((a) => a.item && filterItemsByQuery([a.item], q).length)
		);
	};

	const handleFilterChange = ({
		showBids: sb,
		showAsks: sa,
	}: {
		showBids: boolean;
		showAsks: boolean;
	}) => {
		const both = !sb && !sa;
		setShowBids(both ? true : sb);
		setShowAsks(both ? true : sa);
	};

	const hasBidData = bids.length > 0 && filteredBids.length > 0;
	const hasAskData = asks.length > 0 && filteredAsks.length > 0;

	const visBids =
		(showBids || (!showBids && !showAsks)) && Boolean(hasBidData);
	const visAsks =
		(showAsks || (!showBids && !showAsks)) && Boolean(hasAskData);
	const hasData = filteredBids.length || filteredAsks.length;

	const bidWidthDesktop = visBids ? (visAsks ? '50%' : '100%') : '0%';
	const askWidthDesktop = visAsks ? (visBids ? '50%' : '100%') : '0%';

	const bidTargetWidth = isMobile ? '100%' : bidWidthDesktop;
	const askTargetWidth = isMobile ? '100%' : askWidthDesktop;

	const bidTargetHeight = isMobile
		? visBids
			? MOBILE_COLUMN_PX
			: 0
		: 'auto';
	const askTargetHeight = isMobile
		? visAsks
			? MOBILE_COLUMN_PX
			: 0
		: 'auto';

	if (isLoading) return <TradingFloorSkeleton rows={loadingRows} showFilters={showSearchFilter} />;
	if (!hasData)
		return (
			<Box>
				<TradingFloorFilters
					showSearch={showSearchFilter}
					onSearchChange={handleSearchChange}
					onFilterChange={handleFilterChange}
				/>
				<EmptyState
					header="No Data"
					description="There Are Currently No Active Bids or Asks"
					showButton={false}
				/>
			</Box>
		);
	return (
		<Box>
			<TradingFloorFilters
				showSearch={showSearchFilter}
				onSearchChange={handleSearchChange}
				onFilterChange={handleFilterChange}
				showTypeFilters={asks.length > 0 && bids.length > 0}
			/>

			<Flex direction={{ base: 'column', md: 'row' }} gap={6} maxH={{ base: 'auto', md: `${MOBILE_COLUMN_PX}px` }}>
				{hasBidData && (
					<MotionBox
						initial={false}
						width={bidTargetWidth}
						height={bidTargetHeight}
						animate={{ width: bidTargetWidth, height: bidTargetHeight }}
						transition={{ duration: 0.5, ease: 'easeInOut' }}
						overflow="hidden"
					>
						<ColumnContainer title="Bids" color="blue">
							{filteredBids.map(
								(bid) =>
									bid.item && (
										<ItemBidCard
											key={bid.id}
											item={bid.item}
											bidAmount={bid.amount}
											bidCreatedDays={bid.getCreatedDaysAgo()}
											bidUpdatedDays={bid.getUpdatedDaysAgo()}
											countryCode={bid.getCountryCode()}
											condition={bid.condition}
											expired={bid.isExpired()}
											accepted={bid.isAccepted()}
											complete={bid.isComplete()}
											updated={bid.hasBeenUpdated()}
											useAvatar={false}
											showIcons
											userOwnsBid={bid.userId === user?.id}
											onItemNameClick={onItemNameClick}
											onItemArtistNameClick={onItemArtistNameClick}
											onRemoveClick={() => onRemoveBidClick?.(bid)}
											onEditClick={() => onEditBidClick?.(bid)}
											onContactClick={() => onContactBidClick?.(bid)}
											onSellClick={() => onSellBidClick?.(bid)}
										/>
									)
							)}
						</ColumnContainer>
					</MotionBox>
				)}

				{/* Asks */}
				{hasAskData && (
					<MotionBox
						initial={false}
						width={askTargetWidth}
						height={askTargetHeight}
						animate={{ width: askTargetWidth, height: askTargetHeight }}
						transition={{ duration: 0.5, ease: 'easeInOut' }}
						overflow="hidden"
					>
						<ColumnContainer title="Asks" color="orange">
							{filteredAsks.map(
								(ask) =>
									ask.item && (
										<ItemAskCard
											key={ask.id}
											item={ask.item}
											askAmount={ask.amount}
											askImages={ask.images}
											askCreatedDays={ask.getCreatedDaysAgo()}
											askUpdatedDays={ask.getUpdatedDaysAgo()}
											countryCode={ask.getCountryCode()}
											condition={ask.condition}
											expired={ask.isExpired()}
											accepted={ask.isAccepted()}
											complete={ask.isComplete()}
											updated={ask.hasBeenUpdated()}
											useAvatar={false}
											showIcons
											userOwnsAsk={ask.userId === user?.id}
											onItemNameClick={onItemNameClick}
											onItemArtistNameClick={onItemArtistNameClick}
											onRemoveClick={() => onRemoveAskClick?.(ask)}
											onEditClick={() => onEditAskClick?.(ask)}
											onContactClick={() => onContactAskClick?.(ask)}
											onBuyClick={() => onBuyAskClick?.(ask)}
										/>
									)
							)}
						</ColumnContainer>
					</MotionBox>
				)}
			</Flex>
		</Box>
	);
}
